<template>
  <div
    class="bg-surfaceSaturated text-primary md:p-4 min-md:p-8 mx-auto rounded-2xl max-w-lg"
  >
    <h1
      class="text-2xl font-semibold text-center mb-8 flex items-center justify-center space-x-2"
    >
      <i class="fa-solid fa-unlock-keyhole mr-2" aria-hidden="true"></i>
      <span>{{ $t("passwordReset.title") }}</span>
    </h1>

    <!-- Success Message -->
    <div
      v-if="!loading && success"
      class="text-status-success flex flex-col space-y-2 text-center"
      aria-live="assertive"
    >
      <p class="mb-2 mx-auto max-w-min">
        <i class="fa fa-check-circle fa-2xl"></i>
      </p>
      <p class="text-center">{{ $t("passwordReset.success") }}</p>
      <div class="text-secondary text-center">
        <p class="text-status-info" aria-live="polite">
          {{ $t("common.status.redirecting", { countdown }) }}
        </p>
        <p>
          {{ $t("common.actions.tryAgain") }}
          <router-link
            class="text-invertedAlt font-medium hover:text-inverted focus:text-inverted hover:underline focus:underline select-none"
            v-if="!isAuthenticated"
            to="/log-in"
          >
            {{ $t("passwordReset.links.login") }}
          </router-link>
          <span v-if="!isAuthenticated"> {{ $t("common.or") }} </span>
          <router-link
            class="text-invertedAlt font-medium hover:text-inverted focus:text-inverted hover:underline focus:underline select-none"
            v-if="isAuthenticated"
            to="/your-account"
          >
            {{ $t("passwordReset.links.account") }}
          </router-link>
          <span v-if="isAuthenticated"> {{ $t("common.or") }} </span>
          <router-link
            class="text-invertedAlt font-medium hover:text-inverted focus:text-inverted hover:underline focus:underline select-none"
            to="/"
          >
            {{ $t("passwordReset.links.home") }} </router-link
          >.
        </p>
      </div>
    </div>

    <!-- Error Message -->
    <div
      v-if="!loading && error"
      class="text-status-error flex flex-col space-y-2"
      aria-live="assertive"
    >
      <p class="mb-2 mx-auto max-w-min">
        <i class="fa fa-exclamation-triangle fa-2xl"></i>
      </p>
      <p class="text-center">
        {{ $t("common.status.error") + " " + serverMessage }}
      </p>
      <div class="text-secondary text-center">
        <p>
          {{ $t("common.actions.tryAgain") }}
          <router-link
            class="text-invertedAlt font-medium hover:text-inverted focus:text-inverted hover:underline focus:underline select-none"
            v-if="!isAuthenticated"
            to="/log-in"
          >
            {{ $t("passwordReset.links.login") }}
          </router-link>
          <span v-if="!isAuthenticated"> {{ $t("common.or") }} </span>
          <router-link
            class="text-invertedAlt font-medium hover:text-inverted focus:text-inverted hover:underline focus:underline select-none"
            v-if="isAuthenticated"
            to="/your-account"
          >
            {{ $t("passwordReset.links.account") }}
          </router-link>
          <span v-if="isAuthenticated"> {{ $t("common.or") }} </span>
          <router-link
            class="text-invertedAlt font-medium hover:text-inverted focus:text-inverted hover:underline focus:underline select-none"
            to="/"
          >
            {{ $t("passwordReset.links.home") }} </router-link
          >.
        </p>
        <p>
          {{ $t("common.actions.contactSupport") }}
          <a
            class="text-invertedAlt font-medium hover:text-inverted focus:text-inverted hover:underline focus:underline select-none"
            target="_blank"
            :href="`mailto:${$t('common.company.supportEmail')}?subject=Password Reset Error`"
          >
            {{ $t("common.actions.support") }} </a
          >.
        </p>
      </div>
    </div>

    <!-- Reset Password Form -->
    <form v-if="!success && !error" @submit.prevent="handlePasswordReset">
      <!-- New Password Field -->
      <div class="mt-6">
        <label for="new-password" class="block text-primary font-bold mb-2">
          {{ $t("common.form.newPassword") }}
        </label>
        <div class="flex">
          <input
            :type="passwordFieldType"
            class="w-full bg-background border-primary py-3 px-4 border rounded-l-lg"
            name="password"
            :placeholder="$t('passwordReset.placeholder')"
            autocomplete="new-password"
            autocorrect="off"
            spellcheck="false"
            id="new-password"
            v-model="form.newPassword"
            required
          />
          <button
            type="button"
            class="min-w-[50px] py-3 px-4 bg-brand-darkblue text-white rounded-r-lg border-r border-y border-l-0 border-brand-yellow br-0 hover:text-brand-yellow focus:text-brand-yellow transition-colors duration-200 ease-in-out"
            @click="togglePassword"
            :aria-label="
              passwordFieldType === 'password'
                ? $t('common.actions.showPassword')
                : $t('common.actions.hidePassword')
            "
          >
            <i :class="passwordIcon"></i>
          </button>
        </div>
        <span
          v-if="!isPasswordComplex() && form.newPassword.length > 0"
          class="text-status-error text-center block mt-2"
          aria-live="polite"
        >
          {{ passwordErrorMessage }}
        </span>
      </div>

      <!-- Confirm Password Field -->
      <div class="mt-4">
        <label
          for="confirm-password"
          class="block text-primary font-bold mt-4 mb-2"
        >
          {{ $t("common.form.confirmPassword") }}
        </label>
        <div class="flex">
          <input
            :type="passwordFieldType"
            class="w-full bg-background border-primary py-3 px-4 border rounded-l-lg"
            name="password"
            :placeholder="$t('passwordReset.placeholder2')"
            autocomplete="new-password"
            autocorrect="off"
            spellcheck="false"
            id="confirm-password"
            v-model="form.confirmPassword"
            required
          />
          <button
            type="button"
            class="min-w-[50px] py-3 px-4 bg-brand-darkblue text-white rounded-r-lg border-r border-y border-l-0 border-brand-yellow br-0 hover:text-brand-yellow focus:text-brand-yellow transition-colors duration-200 ease-in-out"
            @click="togglePassword"
            :aria-label="
              passwordFieldType === 'password'
                ? $t('common.actions.showPassword')
                : $t('common.actions.hidePassword')
            "
          >
            <i :class="passwordIcon"></i>
          </button>
        </div>
        <span
          v-if="form.confirmPassword.length > 0 && !isPasswordMatch()"
          class="text-status-error text-center block mt-2"
          aria-live="polite"
        >
          {{ $t("common.validation.passwordMismatch") }}
        </span>
      </div>

      <!-- Submit Button -->
      <button
        type="submit"
        class="w-full mt-10 py-3 px-4 bg-brand-darkblue text-white border border-brand-yellow rounded-lg font-medium text-lg hover:text-brand-yellow focus:text-brand-yellow transition-colors duration-200 ease-in-out disabled:text-grey-light disabled:border-grey"
        :disabled="!isFormValid() || loading"
      >
        {{ $t("passwordReset.submit") }}
      </button>

      <!-- Feedback -->
      <div v-if="serverMessage" :class="[messageClass, 'mt-4 text-center']">
        {{ serverMessage }}
      </div>
    </form>

    <!-- Notification -->
    <NotificationCustom
      v-if="notification.message"
      :type="notification.type"
      :message="notification.message"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import apiClient from "@/api";
import NotificationCustom from "@/components/common/NotificationCustom.vue";

export default {
  components: { NotificationCustom },
  data() {
    return {
      form: {
        newPassword: "",
        confirmPassword: "",
      },
      token: "",
      passwordErrorMessage: "",
      serverMessage: "",
      success: false,
      error: false,
      notification: {
        message: "",
        type: "",
      },
      countdown: 10,
      redirectInterval: null,
      passwordFieldType: "password",
      passwordIcon: "fa-solid fa-eye-slash",
    };
  },
  computed: {
    // Computed property to determine the redirect path based on authentication status
    redirectPath() {
      return this.isAuthenticated ? "/your-account" : "/log-in";
    },
    messageClass() {
      return this.success ? "text-status-success" : "text-status-error";
    },
    ...mapGetters("auth", ["isAuthenticated"]),
  },
  mounted() {
    // Extract the token from the URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    this.token = urlParams.get("token");
    if (!this.token) {
      this.serverMessage = this.$t("common.status.invalidToken");
      this.error = true;
    }
  },
  beforeUnmount() {
    clearInterval(this.redirectInterval);
  },
  methods: {
    togglePassword() {
      this.passwordFieldType =
        this.passwordFieldType === "password" ? "text" : "password";
      this.passwordIcon =
        this.passwordFieldType === "password"
          ? "fa-solid fa-eye-slash"
          : "fa-solid fa-eye";
    },
    isPasswordComplex() {
      const { newPassword } = this.form;
      if (!newPassword) return true;

      const validations = {
        passwordLength: /^.{12,}$/,
        passwordSymbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
        passwordUppercase: /[A-Z]/,
        passwordLowercase: /[a-z]/,
        passwordNumber: /[0-9]/,
      };

      const errorKeys = Object.keys(validations);
      for (const key of errorKeys) {
        if (!validations[key].test(newPassword)) {
          this.passwordErrorMessage = this.$t(`common.validation.${key}`);
          return false;
        }
      }
      this.passwordErrorMessage = "";
      return true;
    },
    isPasswordMatch() {
      return this.form.newPassword === this.form.confirmPassword;
    },
    isFormValid() {
      return this.isPasswordComplex() && this.isPasswordMatch();
    },
    async handlePasswordReset() {
      if (!this.token) {
        this.serverMessage = this.$t("common.status.invalidToken");
        this.error = true;
        this.showNotification("error", this.serverMessage);
        return;
      }

      if (!this.isFormValid()) {
        this.serverMessage = this.$t("passwordReset.formErrors");
        this.error = true;
        this.showNotification("error", this.serverMessage);
        return;
      }

      try {
        // Updated line - token in URL, only newPassword in body
        const response = await apiClient.patch(
          `/auth/reset-password?token=${encodeURIComponent(this.token)}`,
          {
            newPassword: this.form.newPassword,
          },
        );

        this.serverMessage =
          response.data.message || this.$t("passwordReset.success");
        this.success = true;
        this.error = false;
        this.showNotification("success", this.serverMessage);
        this.startRedirectCountdown();
      } catch (error) {
        this.serverMessage =
          error.response?.data?.error || this.$t("common.status.error");
        this.error = true;
        this.success = false;
        this.showNotification("error", this.serverMessage);
      }
    },
    showNotification(type, message) {
      this.notification = { type, message };
    },
    startRedirectCountdown() {
      this.redirectInterval = setInterval(() => {
        this.countdown--;
        if (this.countdown === 0) {
          clearInterval(this.redirectInterval);
          this.$router.push(this.redirectPath);
        }
      }, 1000);
    },
  },
};
</script>

<style scoped>
input {
  transition:
    border-color 0.3s,
    box-shadow 0.3s;
}
input:focus:placeholder-shown {
  border-color: #0080ff !important;
  box-shadow: 0 2px 10px 0.1px rgb(0 128 255 / 0.75) !important;
}
</style>
